/**
 * @file components/dispatch/top-crop-options.ts
 * @description 有機（TOP）申請明細「驗證品項 → 驗證範圍」下拉選項組裝（純函式，便於單測）。
 *
 * 資料來源是後台字典庫「作物管理」。有機與產銷履歷的用詞與層級不同，兩者不可混用：
 *
 * - **有機 TOP**（依「有機農產品有機轉型期農產品類別及品項一覽表」）：
 *   類別（有機農糧產品 / 有機加工品）→ **品項**（米、雜糧、包葉菜、短期葉菜、大漿果…）
 *   → 產品範圍（稻穀 / 白菜、油菜、葉菜甘藷…）。
 *   申請明細要填的【驗證品項】是中間那層「品項」，【驗證範圍】才是底層的個別作物。
 * - **產銷履歷 TAP**：主要類別 → 次要類別（米類、蔬菜類…）→ 品項（水稻、小白菜…），
 *   申請明細逐行直接選到底層品項，不走本檔。
 *
 * 字典落庫方式（`crops` 表）：TOP 的「品項」存 `subCategory`、「產品範圍」的每個作物各存一列
 * 於 `name`，`itemGroup` 全數為 null。
 *
 * ⚠️ 歷史 bug（2026-09-07 線上清單第 5 項）：舊版以 `itemGroup ?? name` 當作品項鍵，
 * 因 TOP 的 itemGroup 恆為 null，【驗證品項】下拉退化成底層作物的平鋪清單，
 * 出現「葉菜甘藷（短期葉菜）」這種把產品範圍當品項的錯誤選項——正解應是「短期葉菜」。
 * 本檔改以「類別＋品項」為鍵修正該層級。
 */

/** 組裝選項所需的作物字典欄位（`CropDto` 的結構子集，方便測試餵假資料） */
export interface TopCropLike {
  mainCategory: string;
  subCategory: string;
  name: string;
}

/**
 * 驗證品項鍵的分隔符。
 *
 * 品項名稱跨類別可能重複（「茶」「咖啡」「其他」在有機農糧產品與有機加工品各有一項），
 * 故下拉 value 必須帶上類別才唯一；分隔符取半形冒號，一覽表所有品項名稱皆不含此字元。
 */
const ITEM_KEY_SEPARATOR = '::';

/** 組出驗證品項鍵：`類別::品項`。 */
export function toTopItemKey(mainCategory: string, subCategory: string): string {
  return `${mainCategory}${ITEM_KEY_SEPARATOR}${subCategory}`;
}

/**
 * 由驗證品項鍵取回品項名稱（落庫用，只存「短期葉菜」不存類別前綴）。
 * 傳入非鍵格式的字串（如舊資料或 AI 辨識的自由文字）時原樣回傳。
 */
export function topItemNameFromKey(itemKey: string): string {
  const idx = itemKey.indexOf(ITEM_KEY_SEPARATOR);
  return idx === -1 ? itemKey : itemKey.slice(idx + ITEM_KEY_SEPARATOR.length);
}

/** 驗證品項下拉的單一選項 */
export interface TopItemOption {
  /** `類別::品項`，見 {@link toTopItemKey} */
  value: string;
  /** 顯示名稱＝品項本身（如「短期葉菜」） */
  label: string;
  /** 搜尋關鍵字：品項 + 所屬類別 + 旗下產品範圍，讓使用者用作物名也能搜到所屬品項 */
  keywords: string;
}

/** 驗證品項下拉的分組（依類別分組，如「有機農糧產品」／「有機加工品」） */
export interface TopItemOptionGroup {
  label: string;
  options: TopItemOption[];
}

/**
 * 由作物字典組出【驗證品項】下拉選項，依類別分組、組內保持字典回傳順序。
 *
 * 搜尋關鍵字含旗下所有產品範圍，故使用者輸入「葉菜甘藷」仍能找到其所屬品項「短期葉菜」
 * （修正前的錯誤選項名稱），不至於因為改版而搜不到東西。
 *
 * @param crops 作物字典（應已按 scheme='TOP' 過濾）
 */
export function buildTopItemOptionGroups(crops: readonly TopCropLike[]): TopItemOptionGroup[] {
  /** 類別 → （品項鍵 → 旗下產品範圍名稱） */
  const byMainCategory = new Map<string, Map<string, string[]>>();
  for (const crop of crops) {
    const mainCategory = crop.mainCategory?.trim();
    const subCategory = crop.subCategory?.trim();
    if (!mainCategory || !subCategory) continue;
    const items = byMainCategory.get(mainCategory) ?? new Map<string, string[]>();
    const key = toTopItemKey(mainCategory, subCategory);
    const scopes = items.get(key) ?? [];
    if (crop.name?.trim()) scopes.push(crop.name.trim());
    items.set(key, scopes);
    byMainCategory.set(mainCategory, items);
  }

  return Array.from(byMainCategory.entries()).map(([mainCategory, items]) => ({
    label: mainCategory,
    options: Array.from(items.entries()).map(([key, scopes]) => ({
      value: key,
      label: topItemNameFromKey(key),
      keywords: [topItemNameFromKey(key), mainCategory, ...scopes].join(' '),
    })),
  }));
}

/**
 * 取某個驗證品項底下可勾選的【驗證範圍】（即一覽表的「產品範圍」）。
 * 未選品項回空陣列（前端據此 disable 範圍下拉）。
 */
export function buildTopScopeOptions(
  crops: readonly TopCropLike[],
  itemKey: string | undefined,
): { value: string; label: string }[] {
  if (!itemKey) return [];
  return crops
    .filter((crop) => toTopItemKey(crop.mainCategory, crop.subCategory) === itemKey)
    .filter((crop) => !!crop.name?.trim())
    .map((crop) => ({ value: crop.name.trim(), label: crop.name.trim() }));
}

/**
 * 由品項名稱反查驗證品項鍵（編輯既有案件、AI 辨識回填時用）。
 *
 * 同名品項跨類別時取字典中第一筆；查不到回 undefined，呼叫端應讓使用者自行重選，
 * 不要把無效值塞進下拉。
 */
export function findTopItemKeyByName(
  crops: readonly TopCropLike[],
  itemName: string | null | undefined,
): string | undefined {
  const target = itemName?.trim();
  if (!target) return undefined;
  const hit = crops.find((crop) => crop.subCategory?.trim() === target);
  return hit ? toTopItemKey(hit.mainCategory, hit.subCategory) : undefined;
}
